import { stripQuoteBrackets } from './ScoringEvidenceOverlay';

const SPLIT_RESULT_KEYS = ['split_strategy', 'split_student_id', 'source_file_name'];
const MIN_HIGHLIGHT_QUOTE_CHARS = 4;

/** 결과의 학생별 쪽(1부터). source_pages가 없으면 pages("3" / "3-4")를 본다. */
export const getSourcePageNumbers = (result) => {
  const src = Array.isArray(result?.source_pages) ? result.source_pages : [];
  const fromList = src
    .map(page => Number.parseInt(page, 10))
    .filter(page => Number.isFinite(page) && page > 0);
  if (fromList.length > 0) return [...new Set(fromList)].sort((a, b) => a - b);
  const match = /^\s*(\d+)(?:\s*-\s*(\d+))?\s*$/.exec(String(result?.pages ?? ''));
  if (!match) return [];
  const start = Number(match[1]);
  const end = match[2] ? Number(match[2]) : start;
  if (start <= 0 || end < start) return [];
  return Array.from({ length: Math.min(end - start + 1, 50) }, (_, i) => start + i);
};

export const formatSourcePages = (pages) => {
  if (!Array.isArray(pages) || pages.length === 0) return '';
  const first = pages[0];
  const last = pages[pages.length - 1];
  const contiguous = pages.every((page, i) => page === first + i);
  if (pages.length === 1) return `${first}쪽`;
  return contiguous ? `${first}~${last}쪽` : `${pages.join(', ')}쪽`;
};

/**
 * 한 파일을 여러 학생으로 나눈 결과인지.
 * 마지막 청크의 학생 1명처럼 경로가 혼자인 결과도 있어 분리 메타데이터도 함께 본다.
 */
export const isSplitFromSharedFile = (result, results = []) => {
  if (!result?.storage_path) return false;
  if (SPLIT_RESULT_KEYS.some(key => result[key] !== undefined && result[key] !== null && result[key] !== '')) return true;
  let shared = 0;
  for (const other of Array.isArray(results) ? results : []) {
    if (other?.storage_path === result.storage_path) {
      shared += 1;
      if (shared >= 2) return true;
    }
  }
  return false;
};

const isPagedSourceFile = (result) => {
  const type = String(result?.file_type || '').toLowerCase();
  const pagedName = /\.(pdf|png|jpe?g|webp|gif|heic|heif)$/;
  return type === 'application/pdf' || type.startsWith('image/')
    || [result?.source_file_name, result?.fileName, result?.filename, result?.storage_path]
      .some(name => pagedName.test(String(name || '').toLowerCase()));
};

/**
 * 원본 대신 학생 본인 답안 글을 먼저 보여 줘야 하는지.
 * - PDF·이미지: 학생별 쪽 정보가 없으면 원본은 1쪽(첫 학생)부터 열린다.
 * - 엑셀·한글·워드: 미리보기가 쪽 이동 없이 파일 전체(모든 학생)를 보여 준다. 쪽 정보도 늘 1쪽이다.
 */
export const shouldShowOwnAnswer = (result, results = []) => {
  if (!isSplitFromSharedFile(result, results)) return false;
  return !isPagedSourceFile(result) || getSourcePageNumbers(result).length === 0;
};

/** AI가 채점 근거로 인용한 학생 답안 문장들(「…」 괄호 제거, 중복 제거). */
export const collectEvidenceQuotes = (result) => {
  const items = [
    ...(Array.isArray(result?.item_scores) ? result.item_scores : []),
    ...(Array.isArray(result?.question_results) ? result.question_results : [])
      .flatMap(question => (Array.isArray(question?.item_scores) ? question.item_scores : [])),
  ];
  const seen = new Set();
  return items
    .flatMap(item => (Array.isArray(item?.evidence_quotes) ? item.evidence_quotes : []))
    .map(quote => stripQuoteBrackets(quote).replace(/\s+/g, ' ').trim())
    .filter(quote => quote.length >= MIN_HIGHLIGHT_QUOTE_CHARS)
    .filter(quote => (seen.has(quote) ? false : seen.add(quote)));
};

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * 답안 글을 인용문 위치 기준으로 [{ text, highlighted }] 조각으로 나눈다.
 * OCR 줄바꿈 차이를 견디도록 공백은 \s+ 로 맞춘다. 겹치는 구간은 합친다.
 */
export const splitAnswerByQuotes = (text, quotes) => {
  const source = String(text || '');
  if (!source) return [];
  const ranges = [];
  (Array.isArray(quotes) ? quotes : []).forEach((quote) => {
    const words = String(quote || '').split(/\s+/).filter(Boolean);
    if (words.length === 0) return;
    const pattern = new RegExp(words.map(escapeRegExp).join('\\s+'), 'g');
    let match = pattern.exec(source);
    while (match) {
      ranges.push([match.index, match.index + match[0].length]);
      if (match[0].length === 0) pattern.lastIndex += 1;
      match = pattern.exec(source);
    }
  });
  if (ranges.length === 0) return [{ text: source, highlighted: false }];
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  ranges.forEach(([start, end]) => {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  });
  const segments = [];
  let cursor = 0;
  merged.forEach(([start, end]) => {
    if (start > cursor) segments.push({ text: source.slice(cursor, start), highlighted: false });
    segments.push({ text: source.slice(start, end), highlighted: true });
    cursor = end;
  });
  if (cursor < source.length) segments.push({ text: source.slice(cursor), highlighted: false });
  return segments;
};
